import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { verifyEmail, resendVerification } from "../api/auth";
import Particles from 'react-tsparticles';
import { loadSlim } from "tsparticles-slim";
import BackgroundShapes from '../components/common/BackgroundShapes';

import {
    Container, Box, Typography, Alert, Button, Paper,
    Stack, CircularProgress, Fade, Avatar, TextField, InputAdornment
} from "@mui/material";

// Icons for different states
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

export default function VerifyEmailPage() {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState("verifying"); // 'verifying', 'success', 'error'
    const [message, setMessage] = useState("Verifying your account, please wait...");
    const [resendEmail, setResendEmail] = useState('');
    const [resendStatus, setResendStatus] = useState({ loading: false, message: '', error: '' });

    // Particle configuration
    const particlesConfig = { /* Paste your particlesConfig object here */ };
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    useEffect(() => {
        const token = searchParams.get("token");
        if (!token) {
            setStatus("error");
            setMessage("Verification link is invalid or missing a token.");
            return;
        }

        verifyEmail(token)
            .then(response => {
                setStatus("success");
                setMessage(response.data.message || "Email verified successfully! You can now log in.");
            })
            .catch(err => {
                setStatus("error");
                setMessage(err.response?.data?.error || "Verification failed. The link may be expired or invalid.");
            });
    }, [searchParams]);

    const handleResendSubmit = async (e) => {
        e.preventDefault();
        setResendStatus({ loading: true, message: '', error: '' });
        try {
            const response = await resendVerification(resendEmail);
            setResendStatus({ loading: false, message: response.data.message, error: '' });
        } catch (err) {
            setResendStatus({ loading: false, message: '', error: err.response?.data?.error || 'Failed to resend email.' });
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'success':
                return (
                    <>
                        <Avatar sx={{ mb: 2, bgcolor: 'success.main', width: 56, height: 56 }}><CheckCircleOutlineIcon sx={{ fontSize: 32 }} /></Avatar>
                        <Typography variant="h5" fontWeight={700} gutterBottom>Verification Successful!</Typography>
                        <Alert severity="success" sx={{ width: '100%' }}>{message}</Alert>
                        <Button component={RouterLink} to="/login" variant="contained" size="large" sx={{ mt: 3 }}>Proceed to Login</Button>
                    </>
                );
            case 'error':
                return (
                    <>
                        <Avatar sx={{ mb: 2, bgcolor: 'error.main', width: 56, height: 56 }}><ErrorOutlineIcon sx={{ fontSize: 32 }} /></Avatar>
                        <Typography variant="h5" fontWeight={700} gutterBottom>Verification Failed</Typography>
                        <Alert severity="error" sx={{ width: '100%' }}>{message}</Alert>

                        <Typography variant="body2" sx={{ mt: 4, mb: 2 }}>Need a new verification link? Enter your email below.</Typography>
                        <Box component="form" onSubmit={handleResendSubmit} sx={{ width: '100%' }}>
                            <Stack spacing={2}>
                                <TextField fullWidth label="Your Email Address" type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} required InputProps={{ startAdornment: (<InputAdornment position="start"><EmailOutlinedIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />
                                <Button type="submit" variant="contained" color="secondary" size="large" disabled={resendStatus.loading}>
                                    {resendStatus.loading ? <CircularProgress size={26} color="inherit" /> : 'Resend Verification Email'}
                                </Button>
                            </Stack>
                        </Box>
                        {resendStatus.message && <Alert severity="success" sx={{ width: '100%', mt: 2 }}>{resendStatus.message}</Alert>}
                        {resendStatus.error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{resendStatus.error}</Alert>}
                    </>
                );
            default: // 'verifying'
                return (
                    <>
                        <Avatar sx={{ mb: 2, bgcolor: 'primary.main', width: 56, height: 56 }}><MailOutlineIcon sx={{ fontSize: 32 }} /></Avatar>
                        <Typography variant="h5" fontWeight={700} gutterBottom>Verifying Email</Typography>
                        <CircularProgress sx={{ my: 2 }} />
                        <Alert severity="info" sx={{ width: '100%' }}>{message}</Alert>
                    </>
                );
        }
    };

    return (
        <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: 'calc(100vh - 128px)', background: `linear-gradient(135deg, #f8faf8, #e6f4ea, #ffecb3)`, backgroundSize: '400% 400%', animation: 'gradientBG 25s ease infinite', '@keyframes gradientBG': { '0%': { backgroundPosition: '0% 50%' }, '50%': { backgroundPosition: '100% 50%' }, '100%': { backgroundPosition: '0% 50%' } }, '@keyframes float': { '0%': { transform: 'translateY(0px) rotate(0deg)' }, '50%': { transform: 'translateY(-20px) rotate(10deg)' }, '100%': { transform: 'translateY(0px) rotate(0deg)' } } }}>
            <BackgroundShapes />
            <Particles id="tsparticles" init={particlesInit} options={particlesConfig} style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 2 }} />

            <Container sx={{ position: 'relative', zIndex: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 128px)' }}>
                <Fade in={true} timeout={1200}>
                    <Paper elevation={8} sx={{ p: { xs: 3, sm: 5 }, maxWidth: 500, width: '100%', backgroundColor: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(12px)', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Stack alignItems="center" spacing={2}>
                            {renderContent()}
                        </Stack>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
}